import {
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import useTranslation from 'Hooks/useTranslation';
import { CourseEmployeeI, CourseEmployeeEditI } from 'types';
import { StyledTableCell, StyledTableRow, StyledTextField } from './CoursesLessonsPaginatedTable.styled';

interface Props {
  employees: CourseEmployeeI[];
  loading?: boolean;
  handleFieldChange?: (id: string, field: string, value: any) => void;
  onPublishChange?: (value: boolean) => void;
  getEmployeeValue?: (employeeId: string, field: string) => any;
  editValues?: CourseEmployeeEditI;
  disabled?: boolean;
  publishAll?: boolean;
}

const CoursesLessonsPaginatedTable = ({
  employees,
  handleFieldChange,
  loading = false,
  getEmployeeValue,
  editValues,
  onPublishChange,
  disabled = false,
  publishAll = false,
}: Props) => {
  const { t2 } = useTranslation('courses_lessons');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading === true) {
    return (
      <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: '20px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            padding: '20px 0',
            gap: '10px',
          }}
        >
          <CircularProgress />
          <Typography variant="h6" color="textSecondary">
            {t2('table.loading')}
          </Typography>
        </div>
      </Paper>
    );
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: '20px', padding: '20px 0' }}>
      <TableContainer>
        <Table size="small" stickyHeader aria-label="tabla de empleados">
          <TableHead>
            <TableRow>
              <StyledTableCell>{t2('table.fullName')}</StyledTableCell>
              <StyledTableCell>{t2('table.department')}</StyledTableCell>
              <StyledTableCell>{t2('table.jobTitle')}</StyledTableCell>
              <StyledTableCell>{t2('table.terminal')}</StyledTableCell>
              <StyledTableCell align="center">{t2('table.grade')}</StyledTableCell>
              <StyledTableCell align="center">
                {t2('table.publish')}
                <Checkbox
                  size="small"
                  checked={publishAll}
                  onChange={e => {
                    const newValue = e.target.checked;
                    onPublishChange?.(newValue);
                  }}
                  disabled={disabled}
                />
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {employees.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(employee => {
              const isPublished = getEmployeeValue?.(employee.id, 'published');
              const grade = getEmployeeValue?.(employee.id, 'grade');

              return (
                <StyledTableRow key={employee.id}>
                  <StyledTableCell>{employee.fullName}</StyledTableCell>
                  <StyledTableCell>{employee.Department.name}</StyledTableCell>
                  <StyledTableCell>{employee.JobTitle.name}</StyledTableCell>
                  <StyledTableCell>{employee.Terminal.name}</StyledTableCell>
                  <StyledTableCell align="center">
                    <StyledTextField
                      type="number"
                      size="small"
                      inputProps={{
                        min: 0,
                        max: 100,
                        step: 10,
                        style: { width: '60px', textAlign: 'center', padding: '4px' },
                      }}
                      value={grade}
                      onChange={e => {
                        const value = e.target.value;
                        // Solo permitir números del 0 al 100 usando regex
                        const regex = /^(100|[1-9]?[0-9])$/;
                        if (value === '' || regex.test(value)) {
                          if (handleFieldChange) {
                            handleFieldChange(employee.id, 'grade', value);
                          }
                        }
                      }}
                      onKeyDown={e => {
                        const allowedKeys = [
                          '0',
                          '1',
                          '2',
                          '3',
                          '4',
                          '5',
                          '6',
                          '7',
                          '8',
                          '9',
                          'Backspace',
                          'Delete',
                          'ArrowLeft',
                          'ArrowRight',
                          'Tab',
                          'Home',
                          'End',
                        ];
                        // Permitir Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                        if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x', 'A', 'C', 'V', 'X'].includes(e.key)) {
                          return;
                        }
                        if (!allowedKeys.includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      disabled={disabled}
                    />
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    <Checkbox
                      size="small"
                      checked={!!isPublished}
                      onChange={e => {
                        const newValue = e.target.checked;
                        if (handleFieldChange) {
                          handleFieldChange(employee.id, 'published', newValue);
                        }
                      }}
                      disabled={disabled}
                    />
                  </StyledTableCell>
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={employees.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Filas por página"
      />
    </Paper>
  );
};

export default CoursesLessonsPaginatedTable;
